
import formatDateBasedTimezoneValue from "@/app/infrastructures/misc/common-library/FormatDateBasedTimezoneValue";
import {
  formatDateNumber,
  parsingErrorResponse
} from "@/app/infrastructures/misc/Utils";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { ManagementLoginApprovalController } from "@/app/ui/controllers/ManagementLoginApprovalController";
import { LoginApprovalHistory, LoginApprovalHistoryRequest } from "@/domain/entities/ManagementLoginApproval";
import { Pagination } from "@/domain/entities/Pagination";
import { defineComponent, onMounted, ref, watch } from "vue";
import ApprovalResultNotFound from "./approval-result-not-found.vue";

export default defineComponent({
  name: "ManagementLoginHistory",
  components: {
    ApprovalResultNotFound
  },
  setup() {
    // --- search --- ///
    const search = ref("");
    const onSearch = (value: string) => {
      search.value = value;
    };
    const clearSearch = () => {
      search.value = "";
    };

    // --- date --- //
    const startDate = ref("");
    const endDate = ref("");

    const getFormattedDate = (date?: string | Date) => {
      const d = date ? new Date(date) : new Date();
      return formatDateNumber(d);
    };

    const setDateRange = (value: Array<string>) => {
      startDate.value = getFormattedDate(value[0]);
      endDate.value = getFormattedDate(value[1]);
    };

    // --- data --- //
    const pagination = ref(new Pagination(1, 10));
    const isLoading = ref(false);
    const errorCause = ref("");
    const loginApprovalHistoryList = ref<LoginApprovalHistory[]>([]);

    const fetchLoginApproval = () => {
      isLoading.value = true;
      errorCause.value = "";
      const params = {
        query: search.value,
        page: pagination.value.page,
        limit: pagination.value.limit,
        startDate: startDate.value,
        endDate: endDate.value
      }

      ManagementLoginApprovalController.getLoginHistoryList(
        new LoginApprovalHistoryRequest(params)
      )
        .then((res) => {
          loginApprovalHistoryList.value = res.data;
          pagination.value = res.pagination;
          ManagementLoginApprovalController.setRefreshList(false);
        })
        .catch((err) => {
          errorCause.value = parsingErrorResponse(err).type;
        })
        .finally(() => {
          isLoading.value = false;
        });
    };

     function getApprovalInfo(item: LoginApprovalHistory) {
      const map: Record<string, { icon: string; text: string; class: string }> = {
        approved: {
          icon: require("@/app/ui/assets/svg/check_circle.svg"),
          text: `<b>Diterima</b> oleh <b>${item.createdBy}</b>`,
          class: "text-green-lp-400"
        },
        removed: {
          icon: require("@/app/ui/assets/svg/trash_red.svg"),
          text: `<b>Dihapus</b> oleh <b>${item.createdBy}</b>`,
          class: "text-red-lp-2000"
        },
        rejected: {
          icon: require("@/app/ui/assets/svg/close_circle.svg"),
          text: `<b>Ditolak</b> oleh <b>${item.createdBy}</b>`,
          class: "text-red-lp-2000"
        },
        expired: {
          icon: require("@/app/ui/assets/svg/clock2.svg"),
          text: `Waktu permintaan login habis`,
          class: ""
        }
      };

      return map[item.approvalStatus];
    }

    // --- table --- //
    const columns = [
      {
        name: "No",
        styleHead: "w-10 text-left whitespace-no-wrap text-sm font-bold text-black-lp-200",
        render: (item: LoginApprovalHistory, index: number) => {
          return `<div class='text-left text-sm text-black-lp-200 font-normal flex justify-center break-words line-clamp-2'>${index +
            1 +
            pagination.value.limit * (pagination.value.page - 1)}</div>`;
        }
      },
      {
        name: "Nama",
        styleHead: "w-32 text-left whitespace-no-wrap text-sm font-bold",
        render: (item: LoginApprovalHistory) => {
          return `
          <div class="flex flex-col gap-1">
            <div class="overflow-ellipsis text-sm text-black-lp-200 font-normal break-words line-clamp-2">${item.accountName}</div>
            <div class="overflow-ellipsis text-sm text-gray-lp-600 font-normal break-words line-clamp-2">${item.roleName}</div>
          </div>`;
        }
      },
      {
        name: "Nama device",
        styleHead: "w-32 text-left whitespace-no-wrap text-sm font-bold",
        render: (item: LoginApprovalHistory) => {
          return `
          <div class="flex flex-col gap-1">
            <div class="overflow-ellipsis text-sm text-black-lp-200 font-normal break-words line-clamp-2">${item.deviceName}</div>
            <div class="overflow-ellipsis text-sm text-gray-lp-600 font-normal break-words line-clamp-2">${item.deviceOs}</div>
          </div>`;
        }
      },
      {
        name: "Kode",
        styleHead: "w-18 text-left whitespace-no-wrap text-sm font-bold",
        render: (item: LoginApprovalHistory) => {
          return `<div class="overflow-ellipsis text-sm text-black-lp-200 font-normal break-words line-clamp-2">${item.pairingCode}</div>`;
        }
      },
      {
        name: "Keterangan",
        styleHead: "w-32 text-left whitespace-no-wrap text-sm font-bold",
        render: (item: LoginApprovalHistory) => {
          const info = getApprovalInfo(item);

          return `
          <div class="flex flex-col gap-1">
            <div class="text-sm text-black-lp-200 font-normal break-words line-clamp-2">
              ${
                info
                  ? `<div class="flex items-center space-x-1">
                      <img src="${info.icon}" class="w-5 h-5" />
                      <span class="${info.class}">${info.text}</span>
                    </div>`
                  : ""
              }
          </div>
            <div class="overflow-ellipsis text-sm text-gray-lp-600 font-normal break-words line-clamp-2">
              ${formatDateBasedTimezoneValue(item.approvedAt)}
            </div>
          </div>`;
        }
      },
    ];

    onMounted(() => {
      fetchLoginApproval()
    });

    // executing fetching data
    watch(
      () => ManagementLoginApprovalController.refreshList,
      (newVal) => {
        if (newVal) {
          pagination.value.page = 1;
          fetchLoginApproval();
        }
      }
    );

    watch(
      [search, startDate, endDate],
      () => {
        pagination.value.page = 1;
        fetchLoginApproval();
      }
    );

    return {
      // --- search --- //
      search,
      onSearch,
      clearSearch,

      // --- date --- //
      startDate,
      endDate,
      setDateRange,
      getFormattedDate,

      // --- data --- //
      loginApprovalHistoryList,

      // --- table --- //
      columns,
      pagination,
      isLoading,
      errorCause,
      fetchLoginApproval
    };
  }
});
